import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  forwardRef
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

import { GenericAction } from '@com-delta-omnichannel/mach';
import { PredictiveSearch } from './models/predictive-search.interfaces';
import { PredictiveSearchAccessibility } from './predictive-search.accessibility';
import { PredictiveSearchExtended } from './predictive-search.extended';
@Component({
  selector: 'idp-predictive-search-mach-core',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './predictive-search.component.html',
  styleUrls: ['./predictive-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IdpPredictiveSearchMachCoreComponent),
      multi: true
    }
  ]
})
export class IdpPredictiveSearchMachCoreComponent
  extends PredictiveSearchExtended
  implements AfterViewInit, ControlValueAccessor, OnInit
{
  @Input() override menuWidth = 220;
  @Input() override content = {} as PredictiveSearch;
  @Output() override choose = new EventEmitter<GenericAction>();
  @Output() override valueChange = new EventEmitter<string>();
  @Output() override isMenuExpanded = new EventEmitter<boolean>();
  @Output() valueChangeOnInput = new EventEmitter();

  predictiveSearchId!: string;
  @ViewChildren('listOption') listOptions!: QueryList<ElementRef>;
  @ViewChild('searchInput', { static: true })
  override searchInput!: ElementRef<HTMLInputElement>;

  constructor(private elementRef: ElementRef) {
    super();
    this.predictiveSearchId = Math.random().toString(36).substring(2, 15);
  }

  ngOnInit(): void {
    this.searchText = this.content.value;
  }

  ngAfterViewInit() {
    this.accessibility = new PredictiveSearchAccessibility(this.listOptions);
    const element: HTMLInputElement =
      this.elementRef.nativeElement.querySelector(
        `#predictive_search_${this.predictiveSearchId}`
      );
    if (element) {
      element.focus({ preventScroll: true });
      if (this.searchText) {
        element.select();
      }
    }
  }

  valueChangeOnInputHandler() {
    this.valueChangeOnInput.emit();
  }

  onInput(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    if (this.accessibility.checkInvalidInput(this.searchText)) {
      this.handleInvalidInput();
      return;
    }
    this.handleValidInput();
  }

  private handleInvalidInput() {
    this.hasError = true;
    this.content.items = [];
    this.valueChange.emit('');
    this.onChange('');
  }

  private handleValidInput() {
    if (this.searchText.length < 3) {
      this.valueChange.emit(this.searchText);
      this.onChange(this.searchText);
      this.content.items = [];
    }
    if (this.searchText.length > 2) {
      this.openDropdown();
    }
    this.hasError = false;
  }

  setError(errorMessage: string) {
    this.hasError = true;
    this.content.errorMessage = errorMessage;
    this.content.items = [];
    this.closeDropdown();
  }

  clearError() {
    this.hasError = false;
    this.content.errorMessage = undefined;
  }

  selectOption(index: number) {
    this.activeOptionIndex = index;
    this.selectedOption = this.content.items[index];
    this.searchText = this.selectedOption.label;
    this.content.items = [];
    this.choose.emit(this.selectedOption);
    this.closeDropdown();
    this.searchInput?.nativeElement?.focus?.();
  }

  handleListOptionKeyDown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      if (event.key === 'Escape') this.closeDropdown();
      return;
    }
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    this.accessibility.handleInputKeydown(event, (index) =>
      this.selectOption(index)
    );
  }
}
